import moment from 'moment';
import { useContext } from 'react';
import { useTheme } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  PropertyStatusesEnum,
  AddressModel,
  DashFeaturesEnum,
  DATE_MONTH_FORMAT,
} from 'dash-common';
import {
  Box,
  Flex,
  Thumbnail,
  Text,
  DropDown,
  Icon,
  TooltipWrapper,
  ModalContext,
  useServerResponseTranslation,
  toast,
  AddressLabel,
  useCheckIfSubscriptionFeatureActiveBroker,
} from 'dash-ui';

import {
  GetBrokerNewPortfolioItemActivitiesDocument,
  NewPortfolioItemActivityFragment,
  useDeleteBrokerActivityByIdMutation,
  useGetPortfolioFolderRootQuery,
} from 'gql/generated.graphql';
import { RoutesPath } from 'flow-components/routes/routes';

import {
  ItemWrapper,
  ThumbnailWrapper,
  TagWrapper,
  LockIconBox,
  PropertyItemDetailsMobileContainer,
  PropertyItemDetailsContainer,
} from './PortfolioItem.styles';

const PropertyItemDetails = ({
  activity,
}: {
  activity: NewPortfolioItemActivityFragment;
}) => {
  const theme = useTheme();
  const { t } = useTranslation(['common', 'pages']);
  const { portfolioItem, createdAt } = activity;
  const { property } = portfolioItem || {};

  return (
    <Flex w="100%">
      <Flex minWidth="fit-content">
        <Text>
          <Text color={theme.colors.n70} as="span">
            {t('pages:dashboardPage.addedToPortfolio.added', {
              date: moment(createdAt).format(DATE_MONTH_FORMAT),
            })}
          </Text>
          <Text color={theme.colors.n70} as="span">
            &nbsp;&middot;&nbsp;
          </Text>
        </Text>
      </Flex>
      <Flex w="inherit" overflow="hidden">
        <AddressLabel
          as="span"
          address={property?.location.address as AddressModel}
          color={theme.colors.n70}
        />
      </Flex>
    </Flex>
  );
};

const PortfolioItem = ({
  activity,
}: {
  activity: NewPortfolioItemActivityFragment;
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { toggleModal, setPayload } = useContext(ModalContext);
  const { t } = useTranslation(['common']);
  const translateServerError = useServerResponseTranslation();
  const { checkFeatureAccess, isSubscriptionActive, triggerSubscriptionModal } =
    useCheckIfSubscriptionFeatureActiveBroker([
      DashFeaturesEnum.PORTFOLIO_CREATION_AND_SHARING,
    ]);
  const { data } = useGetPortfolioFolderRootQuery();

  const portfolioRoot = data?.getPortfolioFolderRoot;
  const [deleteActivity] = useDeleteBrokerActivityByIdMutation({
    refetchQueries: [GetBrokerNewPortfolioItemActivitiesDocument],
  });
  const { _id: activityId, portfolioItem } = activity;
  const {
    property,
    portfolioFolder,
    deleted,
    _id: portfolioId,
  } = portfolioItem || {};

  const file = property?.mainPropertyMediaFile?.files[0];
  const isLocked = property?.status !== PropertyStatusesEnum.PUBLIC;

  const moveToPortfolioFolder = () => {
    if (portfolioFolder?._id === portfolioRoot?._id) {
      return navigate(RoutesPath.Portfolios);
    }
    if (portfolioFolder?._id) {
      return navigate(
        RoutesPath.PortfolioDetails.replace(':id', portfolioFolder._id)
      );
    }
    navigate(RoutesPath.Portfolios);
  };

  return (
    <ItemWrapper flexDirection="column" w="100%" p={3} mb={2}>
      <Flex w="100%" justifyContent="space-between">
        <Flex>
          <ThumbnailWrapper w={80} h={80} overflow="hidden" mr={4}>
            <Thumbnail file={file} />
          </ThumbnailWrapper>
        </Flex>

        <Flex overflow="auto" flexDirection="column" flex={2}>
          <Flex>
            <TagWrapper
              px={2}
              py={1}
              bg={theme.colors.b20}
              mr={2}
              onClick={() => {
                if (isSubscriptionActive) {
                  moveToPortfolioFolder();
                } else {
                  triggerSubscriptionModal();
                }
              }}
            >
              <Text
                color={theme.colors.b50}
                semibold
                textDecoration="underline"
                sm
              >
                {portfolioFolder?.name ||
                  t('pages:dashboardPage.addedToPortfolio.noFolderName')}
              </Text>
            </TagWrapper>
            {deleted && (
              <TagWrapper mr={2} px={2} py={1} bg={theme.colors.n40}>
                <Text semibold color={theme.colors.n80} sm>
                  {t('pages:dashboardPage.addedToPortfolio.removed')}
                </Text>
              </TagWrapper>
            )}
            {isLocked && (
              <TooltipWrapper
                content={t(
                  'pages:dashboardPage.addedToPortfolio.propertyNotAvailable'
                )}
              >
                <LockIconBox
                  data-tooltip-id="private-status"
                  w={32}
                  h={24}
                  justifyContent="center"
                  alignItems="center"
                >
                  <Icon name="lock" size={14} color={theme.colors.n80} />
                </LockIconBox>
              </TooltipWrapper>
            )}
          </Flex>
          <Box my={1}>
            <Text semibold>{property?.name}</Text>
          </Box>
          <PropertyItemDetailsContainer w="100%">
            <PropertyItemDetails activity={activity} />
          </PropertyItemDetailsContainer>
        </Flex>
        <Box>
          <DropDown
            placementHorizontal="left"
            variant="dark"
            Anchor={() => <Icon name="dots" color={theme.colors.n80} size={20} />}
            items={[
              {
                key: 'view',
                children: t('common:labels.view'),
                onClick: () => {
                  if (isSubscriptionActive) {
                    moveToPortfolioFolder();
                  } else {
                    triggerSubscriptionModal();
                  }
                },
              },
              {
                key: 'remove',
                children: t('common:labels.remove'),
                disabled: deleted,
                onClick: () => {
                  if (checkFeatureAccess()) {
                    toggleModal(true, 'RemovePropertyFromPortfolio');
                    setPayload({ portfolioId });
                  }
                },
              },
              {
                key: 'clear',
                children: t('common:labels.clear'),
                onClick: () =>
                  deleteActivity({
                    variables: { activityId },
                    onError: (error) => {
                      toast.error(translateServerError(error));
                    },
                  }),
              },
            ]}
          />
        </Box>
      </Flex>
      <PropertyItemDetailsMobileContainer w="100%" flexDirection="column">
        <PropertyItemDetails activity={activity} />
      </PropertyItemDetailsMobileContainer>
    </ItemWrapper>
  );
};

export default PortfolioItem;
